import {
    DateService,
    DeviceIdService,
    HashStore,
    StateService,
    TrafficSourceService,
    WebToMobileService,
} from '@pulse/browser-shared';
import {
    PulseStorage,
    StorageChangeOrigin,
    TrackerConfigurations,
    isToggleOn,
} from '@pulse/common';
import { BehaviorSubject } from 'rxjs';
import { DebugService } from '../debug.service';
import { AnalyticsSession, RenewedBy } from './session';

export class SessionService {
    session: BehaviorSubject<AnalyticsSession>;
    newSessionGenerated$: BehaviorSubject<number> = new BehaviorSubject(0); // keep last generated time
    private sessionId?: string;
    private lastActivity: number;

    private readonly KEY_LAST_ACTIVITY: string = 'la';
    private webToMobileSessionId?: string;

    constructor(
        private date: DateService,
        private storage: PulseStorage, // Persistent Storage
        private trafficSourceService: TrafficSourceService,
        private hashStore: HashStore,
        private config: TrackerConfigurations,
        private deviceIdService: DeviceIdService,
        private debugService: DebugService,
        private webToMobileService: WebToMobileService,
        private stateService: StateService,
        public storageChange: StorageChangeOrigin,
    ) {
        const webToMobileInfo = this.webToMobileService.getWebToMobileMetaInfo();
        this.lastActivity = Number(this.storage.get(this.KEY_LAST_ACTIVITY) || 0);
        this.webToMobileSessionId = webToMobileInfo?.['session_id'];
        this.sessionId = this.storage.get(this.config.keySessionID) || undefined;

        if (this.webToMobileSessionId) {
            this.storage.set(this.config.keySessionID, this.webToMobileSessionId);
            this.stateService.setState({
                session_id: this.webToMobileSessionId,
            });
        }

        this.registerStorageChangeListeners();
        this.session = new BehaviorSubject(this.getSession());
    }

    getSession(updateLastActivity = true, updateState = false): AnalyticsSession {
        const deviceId = this.deviceIdService.getDeviceId();
        const storedSessionId = this.storage.get(this.config.keySessionID);
        const sessionExpired = this.isSessionExpired();
        const noSession = !this.sessionId;
        let sessionId: string;

        if (this.webToMobileSessionId) {
            sessionId = this.webToMobileSessionId;
        } else {
            sessionId =
                !this.sessionId || sessionExpired
                    ? this.fetchSessionId(sessionExpired)
                    : this.sessionId;
        }

        if (sessionExpired || noSession || updateState || storedSessionId !== sessionId) {
            this.stateService.setState({
                device_id:
                    isToggleOn(this.config.stateEnrichment, 'device_id') ||
                    this.debugService.enabled('debugger')
                        ? deviceId
                        : undefined,
                session_id: isToggleOn(this.config.stateEnrichment, 'session_id')
                    ? sessionId
                    : undefined,
            });
        }

        const state = this.stateService.getStateInternal();
        const lastActivity = this.lastActivity || 0;

        const session: AnalyticsSession = {
            app: this.config.app,
            deviceId,
            sessionId,
            lastActivity,
            state,
        };

        if (updateLastActivity) {
            this.refreshLastActivity();
        }

        if (sessionExpired && storedSessionId) {
            session.renewedBy = 'session_reset';
            session.isRenew = true;
        }

        if (sessionExpired || noSession) {
            this.session?.next(session);
        }

        return session;
    }

    startNewSession(renewedBy?: RenewedBy): string {
        if (this.webToMobileSessionId) {
            this.sessionId = this.webToMobileSessionId;
        }

        this.sessionId = this.generateSessionId();
        this.storage.set(this.config.keySessionID, this.sessionId);
        this.refreshLastActivity();

        if (renewedBy) {
            this.session?.next({ ...this.getSession(true, true), renewedBy });
        }

        this.newSessionGenerated$.next(this.date.now());

        return this.sessionId;
    }

    private refreshLastActivity(): void {
        const now = this.date.now();
        const lastActivity = this.lastActivity || 0;
        const shouldPersist = now - lastActivity > 1000;

        if (!shouldPersist) {
            return;
        }

        this.lastActivity = now;
        this.storage.set(this.KEY_LAST_ACTIVITY, this.lastActivity.toString());
    }

    private isSessionExpired(): boolean {
        if (this.webToMobileSessionId) {
            return false;
        }

        const { sessionTimeoutMS: SESSION_TIMEOUT_MS } = this.config;
        const now = this.date.now();

        const trafficSourceChanged = this.trafficSourceChanged();
        const expired =
            !this.sessionId || now - this.lastActivity > SESSION_TIMEOUT_MS || trafficSourceChanged;

        return expired;
    }

    private trafficSourceChanged(): boolean {
        const lastTrafficSource = this.trafficSourceService.getLastTrafficSource();

        return this.hashStore.changed(
            'session_traffic_source',
            lastTrafficSource as unknown as Record<string, unknown>,
        );
    }

    private fetchSessionId(sessionExpired = false): string {
        if (this.webToMobileSessionId) {
            return this.webToMobileSessionId;
        }

        if (this.sessionId && !sessionExpired) {
            return this.sessionId;
        }
        const newSessionId = this.startNewSession();

        return newSessionId;
    }

    private registerStorageChangeListeners(): void {
        this.storage.change(this.config.keySessionID).subscribe((change) => {
            if (change.value) {
                this.sessionId = change.value;
            }
        });

        this.deviceIdService.getDeviceId$().subscribe((deviceId) => {
            this.stateService.setState({
                device_id:
                    isToggleOn(this.config.stateEnrichment, 'device_id') ||
                    this.debugService.enabled('debugger')
                        ? deviceId
                        : undefined,
            });
        });

        this.storage.change(this.KEY_LAST_ACTIVITY).subscribe(({ value }) => {
            const newTime = Number(value || '0');

            if ((this.lastActivity || 0) < newTime) {
                this.lastActivity = newTime;
            }
        });
    }

    private generateSessionId(): string {
        if (this.webToMobileSessionId) {
            return this.webToMobileSessionId;
        }

        return `${this.date.now()}_${this.deviceIdService.getDeviceId()}`;
    }
}
